import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

// ─── Colores por línea ─────────────────────────────────────────────────────
const LINEA_COLOR = {
  'ACEITES':  '#4cc9f0',
  'FILTROS':  '#f72585',
  'BATERIAS': '#ffc107',
  'ADITIVOS': '#4caf50',
  'BUJIAS':   '#7209b7',
  'CERAS':    '#f4a261',
  'DEFAULT':  '#4361ee',
};
function lineaColor(linea) {
  return LINEA_COLOR[linea?.toUpperCase()] || LINEA_COLOR.DEFAULT;
}

// ─── Helpers ───────────────────────────────────────────────────────────────
function fmt(n)    { return Number(n || 0).toLocaleString('es-EC'); }
function fmtD(n,d=2) { return Number(n || 0).toLocaleString('es-EC', {minimumFractionDigits:d, maximumFractionDigits:d}); }

function StockBadge({ stock }) {
  const color = stock > 10 ? '#4caf50' : stock > 0 ? '#ffc107' : '#e63946';
  const bg    = stock > 10 ? '#0d2218' : stock > 0 ? '#2a1e00' : '#2a0d0d';
  return (
    <span style={{ background: bg, color, border: `1px solid ${color}44`,
      borderRadius: 5, padding: '2px 8px', fontSize: 11,
      fontWeight: 700, fontFamily: 'monospace' }}>
      {fmt(stock)}
    </span>
  );
}

function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 5, background: '#2a2a3e',
        borderRadius: 3, overflow: 'hidden', minWidth: 40 }}>
        <div style={{ width: `${pct}%`, height: '100%',
          background: color, borderRadius: 3 }} />
      </div>
      <span style={{ color: '#aaa', fontSize: 11,
        fontFamily: 'monospace', minWidth: 28,
        textAlign: 'right' }}>{fmt(value)}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Resumen por marca
// ═══════════════════════════════════════════════════════════════════════════
function ResumenMarcas({ marcas, color }) {
  if (!marcas || marcas.length === 0) return null;
  const maxVentas = marcas[0]?.ventas_3m || 1;
  return (
    <div style={{ background: '#13131f', borderRadius: 10,
      padding: '14px 16px', marginBottom: 16 }}>
      <div style={{ color: '#555', fontSize: 10, textTransform: 'uppercase',
        letterSpacing: 1, marginBottom: 12 }}>
        Resumen por marca — últimos 3 meses
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {marcas.slice(0, 8).map((m, i) => (
          <div key={i} style={{ display: 'grid',
            gridTemplateColumns: '120px 1fr 80px 80px 90px',
            alignItems: 'center', gap: 8 }}>
            <span style={{ color: i === 0 ? color : '#aaa',
              fontSize: 12, fontWeight: i === 0 ? 700 : 400,
              whiteSpace: 'nowrap', overflow: 'hidden',
              textOverflow: 'ellipsis' }}>
              {i === 0 && '🏆 '}{m.marca}
            </span>
            <MiniBar value={m.ventas_3m} max={maxVentas} color={color} />
            <span style={{ color: '#555', fontSize: 11,
              textAlign: 'right' }}>{m.items} refs</span>
            <span style={{ color: '#aaa', fontSize: 11,
              textAlign: 'right' }}>
              {fmt(m.stock_total)} stk
            </span>
            <span style={{ color: '#4caf50', fontSize: 11,
              textAlign: 'right' }}>
              ${fmtD(m.revenue_3m)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tabla de items
// ═══════════════════════════════════════════════════════════════════════════
function TablaItems({ items, color }) {
  if (!items || items.length === 0) return (
    <div style={{ color: '#555', textAlign: 'center', padding: 40 }}>
      Sin items para este filtro
    </div>
  );
  const maxVentas = Math.max(...items.map(i => i.ventas_3m), 1);

  return (
    <div style={{ background: '#1e1e2e', borderRadius: 10,
      overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'grid',
        gridTemplateColumns: '2fr 100px 1fr 80px 80px 90px',
        padding: '9px 16px', background: '#13131f',
        borderBottom: '1px solid #0f0f1a', gap: 8 }}>
        {['Producto', 'Marca', 'Ventas 3m', 'Uds. 3m', 'Stock', 'Revenue 3m'].map(h => (
          <span key={h} style={{ color: '#555', fontSize: 10,
            fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.5,
            textAlign: ['Uds. 3m','Stock','Revenue 3m'].includes(h)
              ? 'right' : 'left' }}>{h}</span>
        ))}
      </div>

      {items.map((item, i) => (
        <div key={item.item_id} style={{
          display: 'grid',
          gridTemplateColumns: '2fr 100px 1fr 80px 80px 90px',
          padding: '11px 16px', gap: 8, alignItems: 'center',
          background: i % 2 === 0 ? '#1e1e2e' : '#1a1a2e',
          borderBottom: '1px solid #13131f',
        }}>
          {/* Descripción */}
          <div>
            <div style={{ color: item.ventas_3m > 0 ? '#fff' : '#666',
              fontSize: 12, fontWeight: item.ventas_3m > 50 ? 600 : 400,
              lineHeight: 1.3 }}>
              {item.descripcion}
            </div>
            <div style={{ color: '#444', fontSize: 10, marginTop: 2 }}>
              {item.codbar}
              {item.sublinea && (
                <span style={{ marginLeft: 6, color: '#333' }}>
                  · {item.sublinea}
                </span>
              )}
              {item.unidad && (
                <span style={{ marginLeft: 6, background: '#2a2a3e',
                  color: '#666', borderRadius: 3, padding: '1px 5px',
                  fontSize: 9 }}>{item.unidad}</span>
              )}
            </div>
          </div>

          {/* Marca */}
          <span style={{ color: '#aaa', fontSize: 11,
            whiteSpace: 'nowrap', overflow: 'hidden',
            textOverflow: 'ellipsis' }}>{item.marca}</span>

          {/* Barra ventas */}
          <MiniBar value={item.ventas_3m} max={maxVentas} color={color} />

          {/* Unidades */}
          <span style={{ color: '#aaa', fontSize: 12,
            fontFamily: 'monospace', textAlign: 'right' }}>
            {fmtD(item.unidades_3m, 0)}
          </span>

          {/* Stock */}
          <div style={{ textAlign: 'right' }}>
            <StockBadge stock={item.stock} />
          </div>

          {/* Revenue */}
          <span style={{ color: '#4caf50', fontSize: 12,
            fontFamily: 'monospace', textAlign: 'right' }}>
            {item.revenue_3m > 0 ? `$${fmtD(item.revenue_3m)}` : '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Componente principal: TabAgrupamiento
// ═══════════════════════════════════════════════════════════════════════════
export default function TabAgrupamiento() {
  const [filtros,   setFiltros]   = useState(null);   // líneas y unidades disponibles
  const [linea,     setLinea]     = useState('ACEITES');
  const [sublinea,  setSublinea]  = useState('');
  const [unidad,    setUnidad]    = useState('');
  const [marca,     setMarca]     = useState('');
  const [busqueda,  setBusqueda]  = useState('');
  const [datos,     setDatos]     = useState(null);
  const [cargando,  setCargando]  = useState(false);

  // Cargar filtros disponibles al montar
  useEffect(() => {
    api.get('/productos/agrupamiento/filtros').then(d => {
      if (!d.error) setFiltros(d);
    });
  }, []);

  const cargar = useCallback(() => {
    setCargando(true);
    setDatos(null);
    let url = `/productos/agrupamiento?linea=${encodeURIComponent(linea)}`;
    if (sublinea) url += `&sublinea=${encodeURIComponent(sublinea)}`;
    if (unidad)   url += `&unidad=${encodeURIComponent(unidad)}`;
    if (marca)    url += `&marca=${encodeURIComponent(marca)}`;
    api.get(url)
      .then(d => { setDatos(d.error ? null : d); })
      .finally(() => setCargando(false));
  }, [linea, sublinea, unidad, marca]);

  // Cargar al cambiar línea
  useEffect(() => {
    setSublinea(''); setUnidad(''); setMarca(''); setBusqueda('');
    cargar();
  }, [linea]); // eslint-disable-line

  const color = lineaColor(linea);

  // Filtrar items por búsqueda local
  const items = (datos?.items || []).filter(item => {
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return item.descripcion.toLowerCase().includes(q)
      || item.codbar.toLowerCase().includes(q)
      || item.marca.toLowerCase().includes(q);
  });

  const kpis = datos?.kpis || {};

  return (
    <div>
      {/* ── Filtros principales ─────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16,
        flexWrap: 'wrap', alignItems: 'flex-end' }}>

        {/* Línea (categoría) */}
        <div>
          <div style={labelStyle}>LÍNEA</div>
          <select value={linea}
            onChange={e => setLinea(e.target.value)}
            style={selectStyle}>
            {filtros?.lineas?.map(l => (
              <option key={l} value={l}>{l}</option>
            )) || <option value="ACEITES">ACEITES</option>}
          </select>
        </div>

        {/* Sublínea */}
        <div>
          <div style={labelStyle}>SUBLÍNEA</div>
          <select value={sublinea}
            onChange={e => setSublinea(e.target.value)}
            style={selectStyle}>
            <option value="">Todas</option>
            {datos?.sublineas?.map(sl => (
              <option key={sl} value={sl}>{sl}</option>
            ))}
          </select>
        </div>

        {/* Unidad */}
        <div>
          <div style={labelStyle}>UNIDAD</div>
          <select value={unidad}
            onChange={e => setUnidad(e.target.value)}
            style={selectStyle}>
            <option value="">Todas</option>
            {filtros?.unidades?.map(u => (
              <option key={u.sigla} value={u.sigla}>
                {u.sigla} — {u.descripcion}
              </option>
            ))}
          </select>
        </div>

        {/* Marca */}
        <div>
          <div style={labelStyle}>MARCA</div>
          <select value={marca}
            onChange={e => setMarca(e.target.value)}
            style={selectStyle}>
            <option value="">Todas</option>
            {datos?.marcas?.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        {/* Botón aplicar */}
        <button onClick={cargar} style={{
          background: color, border: 'none', borderRadius: 8,
          color: '#000', padding: '8px 20px',
          fontSize: 12, fontWeight: 700, cursor: 'pointer',
          alignSelf: 'flex-end',
        }}>
          Aplicar
        </button>

        {/* Búsqueda local */}
        <input
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          placeholder="Buscar producto, código..."
          style={{ ...selectStyle, flex: 1, minWidth: 180,
            alignSelf: 'flex-end' }}
        />
      </div>

      {/* ── KPIs ─────────────────────────────────────────────────────── */}
      {datos && (
        <div style={{ display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0,1fr))',
          gap: 10, marginBottom: 16 }}>
          {[
            { label: 'Referencias',   valor: fmt(kpis.total_items),   color: color },
            { label: 'Con stock',     valor: fmt(kpis.con_stock),     color: '#4caf50' },
            { label: 'Sin stock',     valor: fmt(kpis.sin_stock),     color: '#e63946' },
            { label: 'Ventas 3m',     valor: fmt(kpis.ventas_3m),     color: '#4cc9f0' },
            { label: 'Revenue 3m',    valor: `$${fmtD(kpis.revenue_3m)}`, color: '#ffc107' },
          ].map((k, i) => (
            <div key={i} style={{ background: '#1e1e2e', borderRadius: 10,
              padding: '12px 16px', borderTop: `3px solid ${k.color}` }}>
              <div style={{ color: '#555', fontSize: 10,
                textTransform: 'uppercase', letterSpacing: 1 }}>
                {k.label}
              </div>
              <div style={{ color: k.color, fontFamily: 'monospace',
                fontWeight: 700, fontSize: 20, marginTop: 4 }}>
                {k.valor}
              </div>
            </div>
          ))}
        </div>
      )}

      {cargando ? (
        <div style={{ color: '#555', textAlign: 'center', padding: 60 }}>
          Cargando {linea}...
        </div>
      ) : !datos ? (
        <div style={{ color: '#e63946', textAlign: 'center', padding: 40 }}>
          No se pudo cargar. Verifica la conexión.
        </div>
      ) : (
        <>
          <ResumenMarcas marcas={datos.resumen_marcas} color={color} />
          <TablaItems    items={items} color={color} />
          {busqueda && items.length === 0 && (
            <div style={{ color: '#555', textAlign: 'center',
              padding: 24, fontSize: 13 }}>
              Sin resultados para "{busqueda}"
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Estilos ───────────────────────────────────────────────────────────────
const selectStyle = {
  background: '#13131f', border: '1px solid #2a2a3e',
  borderRadius: 7, color: '#fff', padding: '8px 10px',
  fontSize: 12, outline: 'none', cursor: 'pointer',
};
const labelStyle = {
  color: '#555', fontSize: 10, textTransform: 'uppercase',
  letterSpacing: 0.5, marginBottom: 4,
};
